import React, { useCallback, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDeadLetterDetail } from "../hooks/use-dead-letter-detail";

const prettyPrint = (value: unknown) => JSON.stringify(value, null, 2);

const CopyButton: React.FC<{ text: string; label: string }> = ({
  text,
  label,
}) => {
  const [copied, setCopied] = useState<boolean>();
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [text]);
  return (
    <Button size="small" startIcon={<ContentCopyIcon />} onClick={copy}>
      {copied === undefined ? label : copied ? "Copied" : "Copy failed"}
    </Button>
  );
};

const JsonBlock: React.FC<{ title: string; value: unknown }> = ({
  title,
  value,
}) => {
  const text = prettyPrint(value);
  return (
    <Box mb={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="subtitle2">{title}</Typography>
        <CopyButton text={text} label={`Copy ${title.toLowerCase()}`} />
      </Box>
      <Box
        component="pre"
        data-testid={`dead-letter-${title.toLowerCase()}`}
        sx={{
          m: 0,
          p: 1,
          maxHeight: 400,
          overflow: "auto",
          bgcolor: "grey.100",
          borderRadius: 1,
          fontSize: "0.8rem",
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

export const DeadLetterDetails: React.FC<{ id: number }> = ({ id }) => {
  const { detail, loading, error } = useDeadLetterDetail(id);

  if (loading) {
    return (
      <Box p={2}>
        <Typography>Loading payload…</Typography>
      </Box>
    );
  }
  if (error || !detail) {
    return (
      <Box p={2}>
        <Alert severity="error">{error ?? "No details available."}</Alert>
      </Box>
    );
  }
  return (
    <Box p={2}>
      <Typography variant="body2" mb={1}>
        Pub/Sub topic: {detail.pubsubTopic} · Payload size:{" "}
        {detail.payloadSizeBytes} bytes
      </Typography>
      <JsonBlock title="Payload" value={detail.payload} />
      <JsonBlock title="Attributes" value={detail.attributes ?? {}} />
    </Box>
  );
};
